// costOf — the price of one network charge, from its quantity, rate and unit.
//
// Shared by the bill run (server billRunService) and the expected invoice
// (shadowCalculationService). They used to keep separate copies, and the
// expected invoice's copy had no monthly or annual units, so a $/kVA/month
// demand charge showed on NEM Analysis and was missing from Expected Invoice.

export type CostInput = {
  quantity: number;
  rate: number | null;
  price_type: string | null;
  daysBilled: number;
};

/**
 * Price formulas from NEM12_Config_Distribution.xlsx, Definitions tab:
 *   c/kVA/day = cents x kVA value x days billed
 *   c/kW      = cents x kW
 * Rates may be NEGATIVE — the sheet has export rewards at -3.8551 c/kWh and
 * critical-minimum rewards at -103.2. Never clamp.
 */
export const costOf = ({ quantity, rate, price_type, daysBilled }: CostInput): number | null => {
  if (rate === null || price_type === null) return null;
  const unit = price_type.trim().toLowerCase();
  const round2 = (n: number) => Math.round(n * 100) / 100;

  if (unit === '$/day')     return round2(rate * daysBilled);
  if (unit === 'c/kwh')     return round2((rate * quantity) / 100);
  if (unit === 'c/kw' || unit === 'c/kva') return round2((rate * quantity) / 100);
  // The SQL side has priced these since migration 078; costOf did not, so a
  // row carrying one showed a cost on the NEM Analysis screen and nothing on
  // Expected Invoice. Energex prices every volume row in $/kWh.
  if (unit === '$/kwh' || unit === '$/kw' || unit === '$/kva') {
    return round2(rate * quantity);
  }
  if (unit === 'c/kva/day' || unit === 'c/kw/day' || unit === 'c/kwh/day') {
    return round2((rate * quantity * daysBilled) / 100);
  }
  if (unit === '$/kva/day' || unit === '$/kw/day') {
    return round2(rate * quantity * daysBilled);
  }

  // Monthly and annual rates are converted to a daily equivalent and
  // multiplied by the days billed, so a 45-day bill is not charged one month
  // and a 31-day bill is not charged one year. Mirrors migration 115, which
  // does the same in bv_reduce_bucket — the two paths have to agree or
  // Expected Invoice and NEM Analysis show different numbers for one charge.
  const perPeriod = (perYear: number) =>
    (rate * quantity * daysBilled * perYear) / 365;
  if (unit === '$/kva/month' || unit === '$/kw/month') return round2(perPeriod(12));
  if (unit === 'c/kva/month' || unit === 'c/kw/month') return round2(perPeriod(12) / 100);
  if (unit === '$/kva/year' || unit === '$/kw/year') return round2(perPeriod(1));
  if (unit === 'c/kva/year' || unit === 'c/kw/year') return round2(perPeriod(1) / 100);

  return null;   // unknown unit: null, never a guessed number
};
